/**
 * Proactive "app speaks first" greeting — the empty-chat landing POSTs the
 * signed-in user's onboarding context to the server, which builds a background
 * profile of them and returns a warm, dense first message plus a few action
 * chips. When ready, the server also seeds a persisted welcome thread in the
 * sidebar.
 *
 *   POST /api/morrow/greeting
 *     { userId, accountType?, toolkits?, answers?, companyName? }
 *   -> { ready: boolean, greeting: string, suggestions: string[], threadId? }
 *
 * `ready: false` means the profile isn't built yet — the server just kicked off
 * a ~1–2 min profiling probe, so the caller polls again. The fetch helper never
 * throws: on any transport/parse failure it resolves `null` so the caller can
 * quietly keep polling (or fall back to the static landing) instead of erroring.
 */

import type { ThreadId } from "@t3tools/contracts";

import { apiUrl } from "./apiBase";
import type { MorrowAccountType } from "./onboardingStore";

export interface MorrowGreetingRequest {
  readonly userId: string;
  /**
   * Active Clerk org id (`org_…`), when the user is in one. Sent so the deep
   * probe can build the ORGANIZATION profile (→ org doc) alongside the personal
   * one; without it the probe is person-only. Ignored server-side unless it's a
   * real org id AND accountType is "company".
   */
  readonly orgId?: string;
  readonly accountType?: MorrowAccountType;
  readonly toolkits?: readonly string[];
  readonly answers?: Record<string, string>;
  readonly companyName?: string;
  /** Company domain the user entered — the richest signal onboarding captured. */
  readonly companyWebsite?: string;
  /** Short description of the company looked up from that domain. */
  readonly companyBrief?: string;
  /** Coarse company focus ("marketing" | "product" | "sales" | "general"). */
  readonly companySituation?: string;
}

export interface MorrowGreetingResult {
  readonly ready: boolean;
  readonly greeting: string;
  readonly suggestions: readonly string[];
  readonly threadId?: ThreadId;
}

export interface MorrowGreetingRecord {
  readonly threadId: ThreadId | null;
  readonly suggestions: readonly string[];
  /** Legacy overlay-only accounts that greeted before welcome threads existed. */
  readonly legacyShown: boolean;
}

function parseThreadId(value: unknown): ThreadId | undefined {
  return typeof value === "string" && value.trim().length > 0
    ? (value.trim() as ThreadId)
    : undefined;
}

function parseGreetingResult(value: unknown): MorrowGreetingResult | null {
  if (!value || typeof value !== "object") return null;
  const record = value as {
    ready?: unknown;
    greeting?: unknown;
    suggestions?: unknown;
    threadId?: unknown;
  };
  const greeting = typeof record.greeting === "string" ? record.greeting : "";
  const suggestions = Array.isArray(record.suggestions)
    ? record.suggestions.filter((s): s is string => typeof s === "string" && s.trim().length > 0)
    : [];
  const threadId = parseThreadId(record.threadId);
  return {
    ready: record.ready === true,
    greeting,
    suggestions,
    ...(threadId ? { threadId } : {}),
  };
}

/**
 * POST the greeting request. Returns the parsed result, or `null` on any
 * failure (network error, non-2xx, unparseable body) — never throws.
 */
export async function fetchMorrowGreeting(
  request: MorrowGreetingRequest,
  signal?: AbortSignal,
): Promise<MorrowGreetingResult | null> {
  try {
    const res = await fetch(apiUrl("/api/morrow/greeting"), {
      method: "POST",
      headers: { accept: "application/json", "content-type": "application/json" },
      body: JSON.stringify({
        userId: request.userId,
        ...(request.orgId && request.orgId.trim().length > 0
          ? { orgId: request.orgId.trim() }
          : {}),
        ...(request.accountType ? { accountType: request.accountType } : {}),
        ...(request.toolkits && request.toolkits.length > 0
          ? { toolkits: [...request.toolkits] }
          : {}),
        ...(request.answers && Object.keys(request.answers).length > 0
          ? { answers: request.answers }
          : {}),
        ...(request.companyName && request.companyName.trim().length > 0
          ? { companyName: request.companyName.trim() }
          : {}),
        ...(request.companyWebsite && request.companyWebsite.trim().length > 0
          ? { companyWebsite: request.companyWebsite.trim() }
          : {}),
        ...(request.companyBrief && request.companyBrief.trim().length > 0
          ? { companyBrief: request.companyBrief.trim() }
          : {}),
        ...(request.companySituation && request.companySituation.trim().length > 0
          ? { companySituation: request.companySituation.trim() }
          : {}),
      }),
      ...(signal ? { signal } : {}),
    });
    if (!res.ok) {
      console.warn(`[morrow-greeting] request failed: ${res.status}`);
      return null;
    }
    return parseGreetingResult(await res.json());
  } catch (err) {
    // Aborts are expected on unmount / dep change; don't warn on those.
    if (!(err instanceof DOMException && err.name === "AbortError")) {
      console.warn("[morrow-greeting] request error", err);
    }
    return null;
  }
}

/**
 * Once-per-account gate keyed by the stable user id. When a welcome thread id is
 * stored we stop polling and overlay rendering; legacy `shown` entries (overlay
 * only) still allow one more poll so the server can seed a persisted thread.
 */
// v3: discards the v2 markers written by the old up-front-marker bug, which
// permanently suppressed the greeting/knowledge loader for accounts that never
// got a welcome thread seeded. With v2 ignored, the gate re-evaluates from the
// server's `knowledgeReady` truth and poisoned accounts heal on next load.
const GREETING_RECORD_PREFIX = "morrow:greeting:v3:";

function greetingRecordKey(userId: string): string {
  return `${GREETING_RECORD_PREFIX}${userId}`;
}

function safeStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

function parseStoredRecord(raw: string): MorrowGreetingRecord | null {
  if (raw === "shown") {
    return { threadId: null, suggestions: [], legacyShown: true };
  }
  try {
    const parsed = JSON.parse(raw) as {
      threadId?: unknown;
      suggestions?: unknown;
    };
    const threadId = parseThreadId(parsed.threadId) ?? null;
    const suggestions = Array.isArray(parsed.suggestions)
      ? parsed.suggestions.filter((s): s is string => typeof s === "string" && s.trim().length > 0)
      : [];
    return { threadId, suggestions, legacyShown: false };
  } catch {
    return null;
  }
}

export function getMorrowGreetingRecord(userId: string): MorrowGreetingRecord | null {
  if (!userId) return null;
  try {
    const raw = safeStorage()?.getItem(greetingRecordKey(userId));
    if (!raw) return null;
    return parseStoredRecord(raw);
  } catch {
    return null;
  }
}

/** True when a welcome thread was already seeded for this account. */
export function morrowWelcomeThreadSeeded(userId: string): boolean {
  return getMorrowGreetingRecord(userId)?.threadId != null;
}

export function writeMorrowGreetingRecord(
  userId: string,
  record: { readonly threadId: ThreadId; readonly suggestions: readonly string[] },
): void {
  if (!userId) return;
  try {
    safeStorage()?.setItem(
      greetingRecordKey(userId),
      JSON.stringify({
        threadId: record.threadId,
        suggestions: [...record.suggestions],
      }),
    );
  } catch {
    // Best-effort; a session that can't persist may greet again next visit.
  }
}

/**
 * Terminal give-up marker. Called ONLY when the greeting reached a terminal
 * non-seeding state — the server polled the full window without ever seeding a
 * welcome thread, or answered `ready` without a thread id. Writes a threadId-less
 * record so a never-ready server doesn't replay the loader on every future draft.
 *
 * Crucially this is NOT written up front: the happy path persists nothing until
 * the thread is actually seeded, so a remount mid-profiling re-enters the loader
 * and keeps polling until the knowledge record exists (the bug the old up-front
 * marker caused — it disabled the loader before the record was ever created).
 * Never clobbers a richer record that already carries a thread id + suggestions.
 */
export function markMorrowGreetingGaveUp(userId: string): void {
  if (!userId) return;
  if (morrowWelcomeThreadSeeded(userId)) return;
  try {
    safeStorage()?.setItem(
      greetingRecordKey(userId),
      JSON.stringify({ threadId: null, suggestions: [] }),
    );
  } catch {
    // Best-effort; a session that can't persist may greet again next visit.
  }
}
